// typescript
import React, { useCallback, useEffect, useState } from 'react';
import debounce from "lodash/debounce";
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';
import theme from '@/mui/theme';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import { getDefaultSortOrder } from '@/utils/slug';
import { FilterSliderData } from '@/types/common';

interface FilterSliderProps {
  title: string;
  sliderData: FilterSliderData;
  storeData: {
    storeId: string;
    langId: string;
    catalogId: string;
    locale: string;
    categoryId: string;
    pageNumber: string;
    pageSize: string;
  };
  handleFilter: Function;
  /**
   * facets: Array dei filtri attualmente attivi (ultima query verso graphql HCL)
   *
   * Viene usato per:
   * 1. Determinare quali mark sono attualmente nel filtro
   * 2. Mantenere i filtri da altri facet quando si modifica questo slider
   * 3. Sincronizzare lo stato esterno quando cambiano (parent component)
   */
  facets?: any;
}

/**
 * findNearestIndex: trova l'indice più vicino nel array di mark
 *
 * @param array - Array di valori di mark ordinati
 * @param value - Valore da cercare
 * @param isMax - Se true, riporta l'indice del mark subito precedente per il massimo
 *                Se false, riporta l'indice esatto del mark più vicino per il minimo
 *
 * Logica: itera l'array e trova il valore con la minima differenza
 * Per il massimo, sceglie l'indice precedente per evitare di saltare oltre
 */
const findNearestIndex = (array: number[], value: number, isMax: boolean) => {
  let nearestIndex = 0;
  let smallestDiff = Math.abs(array[0] - value);
  array.forEach((v, i) => {
    const diff = Math.abs(v - value);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      nearestIndex = isMax ? Math.max(0, i - 1) : i;
    }
  });
  return nearestIndex;
};

/**
 * sanitizeFacet: converte i valori da formato HCL a formato leggibile
 * - Sostituisce + con spazio (es: "100+lm" → "100")
 * - Sostituisce \" con "
 */
const sanitizeFacet = (facet: string) => facet.replace(/\+/g, " ").replace(/\\"/g, '"');

/**
 * Componente FilterSlider
 * Implementa un range slider relativo ad una facetta (es. "Flusso luminoso uscente")
 * con valori snappati su mark predefiniti (entry della facetta).
 * Permette di filtrare per intervalli di valori.
 *
 * Funzionalità:
 * - Range slider con track visuale tra min e max selezionati
 * - Input numerici per inserire direttamente i valori
 * - Snap automatico sui valori di mark predefiniti
 * - Debounce dell'API call per evitare troppe richieste
 * - Sincronizzazione con filtri globali
 */
const FilterSlider: React.FC<FilterSliderProps> = ({
  title,
  sliderData,
  storeData,
  handleFilter,
  facets,
}) => {

  const {
    valueMin = 1,
    valueMax = 100,
    label,
    marks,
    marksValues,
    isReset,
    sliderCurrentValue = undefined,
    facetId, // Identificatore univoco del facet per il tracciamento dei filtri
  } = sliderData;


  // Calcola il valore minimo di default:
  // 1. Se valueMin è un numero, usalo
  // 2. Altrimenti usa il primo valore in marksValues
  // 3. Fallback a 0 se non ci sono valori disponibili
  const defaultMin =
    typeof valueMin === 'number'
      ? valueMin
      : Array.isArray(marksValues) && marksValues.length
        ? marksValues[0]
        : 0;

  // Calcola il valore massimo di default:
  // 1. Se valueMax è un numero, usalo
  // 2. Altrimenti usa l'ultimo valore in marksValues
  // 3. Fallback a defaultMin se non ci sono valori disponibili
  const defaultMax =
    typeof valueMax === 'number'
      ? valueMax
      : Array.isArray(marksValues) && marksValues.length
        ? marksValues[marksValues.length - 1]
        : defaultMin;

  // Determina il range iniziale dello slider:
  // Se sliderCurrentValue è un array di 2 elementi, usalo
  // Altrimenti usa [defaultMin, defaultMax]
  const initialRange = Array.isArray(sliderCurrentValue) && sliderCurrentValue.length === 2
    ? sliderCurrentValue
    : [defaultMin, defaultMax];

  // displayValue: valore "libero" dello slider e dell'input e mostrato come recap tra i filtri attivi
  // Si contrappone a snappedValue che è il valore effettivamente snappato sui mark (valori effettivi previsti dalla faccetta)
  // Aggiornato in tempo reale per feedback immediato all'utente
  const [displayValue, setDisplayValue] = useState<number[]>(initialRange);

  // // snappedValue: valore effettivamente snappato sui mark
  // // Usato come range per individuare le entry da passare all'API in caso si attivi questo filtro
  // const [snappedValue, setSnappedValue] = useState<number[]>(initialRange);

  /**
   * debouncedHandleFilter: versione debounced dell'API call per il filtro
   * - Debounce di 500ms per evitare troppe richieste durante il drag dello slider
   * - Aspetta che l'utente smetta di interagire prima di effettuare la richiesta
   * - Migliora le performance e riduce il carico al server
   */
  const debouncedHandleFilter = useCallback(debounce((queryParams: any) => {
    console.log('=== FilterSlider debouncedHandleFilter ===');
    console.log('queryParams:', queryParams);
    console.log('=========================================');
    handleFilter(queryParams);
  }, 500), [handleFilter]);

  /**
   * useEffect: sincronizza lo stato interno quando cambiano i filtri esterni
   *
   * Casi gestiti:
   * 1. isReset === true: resetta a [defaultMin, defaultMax]
   * 2. sliderCurrentValue cambia: aggiorna displayValue per riflettere il nuovo valore esterno
   */
  useEffect(() => {
    if (isReset === true) {
      const min = typeof valueMin === 'number' ? valueMin : marksValues?.[0] ?? 0;
      const max = typeof valueMax === 'number' ? valueMax : marksValues?.[marksValues.length - 1] ?? min;
      setDisplayValue([min, max]);
      // setSnappedValue([min, max]);
      return;
    }

    if (Array.isArray(sliderCurrentValue) && sliderCurrentValue.length === 2) {
      setDisplayValue(sliderCurrentValue);
      // setSnappedValue(sliderCurrentValue);
      return;
    }
  }, [isReset, valueMin, valueMax, marksValues, sliderCurrentValue]);

  /**
   * handleChange: gestisce gli eventi di cambio valore durante il drag dello slider
   *
   * - Aggiorna displayValue per il feedback visuale immediato
   * - NON effettua l'API call (quella parte solo su onChangeCommitted)
   */
  const handleChange = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    setDisplayValue(newValue as number[]);
  };

  /**
   * handleChangeCommitted: gestisce il rilascio del mouse o il commit da input (Enter/Blur)
   *
   * Flusso:
   * 1. Converte newValue in range [min, max]
   * 2. Trova gli indici più vicini nei mark tramite findNearestIndex
   * 3. Snap automatico ai valori di mark: [marksValues[minNearestIndex], marksValues[maxNearestIndex]]
   * 4. Costruisce l'array di filtri: combina i filtri base con i mark interni al range
   * 5. Se ci sono mark nel range, effettua l'API call con debounce
   * 6. Aggiorna snappedValue per riflettere il nuovo stato
   *
   * Dettagli importanti:
   * - sanitizeFacet: rimuove caratteri speciali dai valori (+ → spazio, \" → ")
   * - marksInRange: tutti i mark tra min e max selezionati
   * - filterValueRange: i valori da passare all'API per questo facet
   * - baseFilters: filtri da altri facet che vengono mantenuti
   * - filterValue: elenco completo dei filtri (da altri facet + questo slider)
   */
  const handleChangeCommitted = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    console.log('=== FilterSlider handleChangeCommitted ===');
    if (!marks || !marksValues) {
      console.error('marks or marksValues missing');
      return;
    }

    console.log('facetId:', facetId);

    // Ensure newValue is an array
    const range = Array.isArray(newValue) ? newValue : [newValue, newValue];
    console.log('range (display):', range);

    // Trova gli indici dei mark interni al range dei valori di min/max selezionati
    const minNearestIndex = findNearestIndex(marksValues, range[0], false);
    const maxNearestIndex = findNearestIndex(marksValues, range[1], true);

    // Snap ai valori effettivi dei mark
    const snapped = [marksValues[minNearestIndex], marksValues[maxNearestIndex]];
    console.log('snapped:', snapped);

    // setSnappedValue(snapped);
    setDisplayValue(range);

    // Tutti i mark che rientrano nel range selezionato
    const marksInRange = marks.slice(minNearestIndex, maxNearestIndex + 1);
    console.log('marksInRange:', marksInRange);

    // Estrae i valori per la query dall'API per questo slider
    // usando valueForQuery che è la chiave nel formato aspettato dal backend
    // (stringa con value e unità di misura)
    const filterValueRange = marksInRange.map((m: any) => sanitizeFacet(m.valueForQuery));
    console.log('filterValueRange:', filterValueRange);

    // Filtri attualmente attivi da altri facet
    const currentFilters = Array.isArray(facets) ? facets : [];
    console.log('currentFilters:', currentFilters);

    // Tutti i possibili valori che questo slider può aggiungere ai filtri
    const sliderPossibleValues = marks.map((m: any) => sanitizeFacet(m.valueForQuery));
    console.log('sliderPossibleValues:', sliderPossibleValues);

    // filtri da altri facet senza quelli possibili relativi a questo slider
    const baseFilters = currentFilters.filter((f: string) => !sliderPossibleValues.includes(f));
    console.log('baseFilters:', baseFilters);

    // Nuovo array di filtri: filtri da altri facet + filtri dal nuovo range
    const filterValue = [...baseFilters, ...filterValueRange];
    console.log('filterValue (final):', filterValue);

    // DUBBIO: anche range vuoti forse dovrebbero fare la chiamata!?
    if (filterValueRange.length > 0) {
      const queryParams = {
        storeId: storeData.storeId,
        langId: storeData.langId,
        catalogId: storeData.catalogId,
        locale: storeData.locale,
        categoryId: storeData.categoryId,
        filterValue: filterValue,
        sliderFilterValueDeleted: false,
        displayValue: range,
        facetId: facetId, // Identifica quale slider è stato modificato
        pageNumber: storeData.pageNumber,
        pageSize: storeData.pageSize,
        orderBy: getDefaultSortOrder(),
      };
      debouncedHandleFilter(queryParams);
    }
    console.log('=====================================');
  };

  /**
   * handleInputChange: gestisce la modifica del valore tramite input numerico
   *
   * @param index - 0 per min, 1 per max
   * @param newVal - Nuova stringa inserita dall'utente
   *
   * - Valida che il valore sia numerico
   * - Aggiorna solo displayValue (il valore mostrato nell'input)
   * - Aspetta handleInputCommit per effettuare lo snap e l'API call
   */
  const handleInputChange = (index: number, newVal: string) => {
    const numericVal = Number(newVal);
    if (isNaN(numericVal)) return;
    setDisplayValue((prev) => {
      const updated = [...prev];
      updated[index] = numericVal;
      return updated;
    });
  };

  /**
   * handleInputCommit: finalizza l'input numerico dell'utente
   *
   * Chiama handleChangeCommitted con il displayValue attuale,
   * che a sua volta:
   * - Snappa ai mark più vicini
   * - Effettua l'API call
   * - Aggiorna lo stato globale dei filtri
   */
  const handleInputCommit = () => {
    handleChangeCommitted({} as any, displayValue);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Slider range con track blu tra min e max selezionati */}
      <StyledSlider
        getAriaLabel={() => title}
        min={valueMin ?? defaultMin}
        max={valueMax ?? defaultMax}
        step={1}
        valueLabelDisplay="off"
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        value={displayValue}
      />

      {/* Box con due input numerici per inserire manualmente min e max */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} sx={{ mt: 1, overflow: 'hidden', flexWrap: 'wrap', maxWidth: '100%' }}>
        {/* Input minimo */}
        <Box display="flex" alignItems="center" sx={{ maxWidth: '100%' }}>
          <StyledBoxVal
            component="input"
            {...({ type: "number", value:displayValue[0] } as React.InputHTMLAttributes<HTMLInputElement>)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(0, e.target.value)}
            onBlur={handleInputCommit}
            onKeyDown={(e) => e.key === 'Enter' && handleInputCommit()}
            aria-label={`${title}-min`}
          />
          {label && <Typography ml={1}>{label}</Typography>}
        </Box>

        {/* Input massimo */}
        <Box display="flex" alignItems="center" sx={{ maxWidth: '100%' }}>
          <StyledBoxVal
            component="input"
            {...({ type: "number", value: displayValue[1] } as React.InputHTMLAttributes<HTMLInputElement>)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(1, e.target.value)}
            onBlur={handleInputCommit}
            onKeyDown={(e) => e.key === 'Enter' && handleInputCommit()}
            aria-label={`${title}-max`}
          />
          {label && <Typography ml={1}>{label}</Typography>}
        </Box>
      </Box>
    </Box>
  );
};

const StyledSlider = styled(Slider)({
  paddingBottom: '8px',
  '.MuiSlider-rail': {
    backgroundColor: '#dfdfdf',
    height: '2px',
  },
  '.MuiSlider-track': {
    backgroundColor: '#1088ce',
    color: '#1088ce',
    height: '0',
  },
  '.MuiSlider-thumb': {
    backgroundColor: '#ffffff',
    '&:hover': {
      boxShadow: '0px 0px 0px 8px rgb(16 136 206 / 16%)',
    },
  },
});

const StyledBoxVal = styled(Box)({
  padding: '6px 97px 6px 10px',
  border: '1px solid #e4e4e4',
  fontSize: '14px',
  letterSpacing: '0',
  minWidth: '0',
  flexGrow: '0',
  flexShrink: '1',
  [theme.breakpoints.up('sm')]: {
    padding: '9px 21px 9px 10px',
  },
});

export default FilterSlider;
